import React, { useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { Anchor, Button, Card, FormError } from '@hcs/design-system';
import { AccountFields, LoginPayload } from '@hcs/types';
import { URL_PARAM_REDIRECT } from '@hcs/webapps';

import { LoginNeedsEmailConfirmationError } from '../../api';
import { ACCOUNT_FIELD_CONFIGS } from '../../constants';
import { AccountFieldInput } from '../../features/AccountFieldInput';
import { useLogin, UseLoginOptions } from '../../hooks/useLogin';

import styles from './Login.module.css';

const yupSchema = yup.object().shape({
  [AccountFields.Email]: ACCOUNT_FIELD_CONFIGS[AccountFields.Email].validator,
  [AccountFields.Password]:
    ACCOUNT_FIELD_CONFIGS[AccountFields.Password].validator,
});

const dataHcName = 'login-page-login';
export interface Props {
  // Callback when clicking the Forgot Password link
  navigateToForgotPassword?: VoidFunction;
  className?: string;
  preventRedirect?: boolean;
  onLoginSuccess?: UseLoginOptions['onSuccess'];
}

export const Login = ({
  className,
  preventRedirect,
  onLoginSuccess,
  navigateToForgotPassword,
}: Props) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectParam = searchParams.get(URL_PARAM_REDIRECT);
  const form = useForm<LoginPayload>({
    resolver: yupResolver(yupSchema),
    mode: 'onBlur',
    reValidateMode: 'onChange',
  });

  const [loginFailedMessage, setLoginFailedMessage] = useState<
    string | undefined
  >();

  const { mutate: login, isLoading } = useLogin({
    onError(error) {
      let errorMessage = 'Unknown email or password';
      if (error instanceof LoginNeedsEmailConfirmationError) {
        errorMessage = error.message;
      } else if (error instanceof Error && error.message) {
        errorMessage = error.message;
      }

      setLoginFailedMessage(errorMessage);
    },
    onSuccess(response, loginPayload) {
      form.clearErrors();
      form.reset();
      onLoginSuccess?.(response, loginPayload);
      if (redirectParam && !preventRedirect) {
        navigate(redirectParam, { replace: true });
      }
    },
  });
  const { formState } = form;

  const handleLoginSubmit = (data: LoginPayload) => {
    login(data);
  };

  const handleFieldChange = (
    value: string,
    onChange: (v: string) => void
  ): void => {
    onChange(value);
    if (loginFailedMessage) {
      setLoginFailedMessage(undefined);
    }
  };

  return (
    <Card dataHcName={dataHcName} className={className}>
      {loginFailedMessage && (
        <FormError
          // Hardcoded to prevent current automation from breaking
          dataHcName={'login-errors'}
          className={styles.Error}
          value={<div>{loginFailedMessage}</div>}
        />
      )}
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(handleLoginSubmit)}>
          <Controller
            name={AccountFields.Email}
            control={form.control}
            render={({ field }) => (
              <AccountFieldInput
                {...field}
                autoComplete="on"
                // Hardcoded to prevent automation from breaking
                dataHcName="login-page-login"
                onChange={(value: string) =>
                  handleFieldChange(value, field.onChange)
                }
                error={form.formState.errors[field.name]?.message}
              />
            )}
          />
          <Controller
            name={AccountFields.Password}
            control={form.control}
            render={({ field }) => (
              <AccountFieldInput
                {...field}
                autoComplete="on"
                onChange={(value: string) => {
                  handleFieldChange(value, field.onChange);
                }}
                // Hardcoded to prevent automation from breaking
                dataHcName="login-page-login"
                error={form.formState.errors[field.name]?.message}
              />
            )}
          />
          {navigateToForgotPassword && (
            <div className={styles.ForgotPassword}>
              <Anchor
                dataHcName={`${dataHcName}-forgot-password`}
                onClick={navigateToForgotPassword}
              >
                Forgot your password?
              </Anchor>
            </div>
          )}
          <Button
            dataHcName={`${dataHcName}-submit-button`}
            label="Login"
            disabled={!formState.isValid || isLoading}
          />
        </form>
      </FormProvider>
    </Card>
  );
};
